import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Boxes, FolderGit2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { FleetClientService, type PracticeReposResult } from '../services/fleetClientService';

function grade(score: number | null): string {
  if (score == null) return '—';
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

function gradeTone(g: string): string {
  if (g === 'A' || g === 'B')
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (g === 'C') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  if (g === 'D' || g === 'F') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
}

function PracticeSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="mt-8 mb-6 h-44 rounded-3xl bg-gray-100 dark:bg-gray-800/60" />
      <div className="h-72 rounded-2xl bg-gray-100 dark:bg-gray-800/60" />
    </div>
  );
}

export const PracticeReposPage: React.FC = () => {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const [params] = useSearchParams();
  const practiceName = params.get('name') || slug?.replace(/_/g, ' ') || 'Practice';
  const [data, setData] = useState<PracticeReposResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id || !slug) return;
    try {
      const res = await FleetClientService.reposUsingPractice(id, slug);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load repositories');
    } finally {
      setLoading(false);
    }
  }, [id, slug]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Practice" showDivider={true} />
        <main className="pt-(--navbar-offset) max-w-4xl mx-auto px-4 pb-20">
          <PracticeSkeleton />
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
        <Navbar pageTitle="Practice" showDivider={true} />
        <div className="text-center pt-32 text-red-500">{error || 'Not found'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
      <Navbar pageTitle={`${practiceName} — repositories`} showDivider={true} />
      <main className="pt-(--navbar-offset) max-w-4xl mx-auto px-4 pb-20">
        {/* Hero header */}
        <div className="relative mt-8 mb-6 overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-teal-50 via-white to-white dark:from-teal-950/30 dark:via-gray-900/40 dark:to-gray-900/40 p-6 sm:p-8">
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Link
                to={`/orgs/${id}/fleet`}
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Fleet health
              </Link>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                {practiceName}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {data.repositories.length} of {data.totalInOrg} repositories in {data.orgName} use
                this practice.
              </p>
            </div>
            {/* Adoption stat tile */}
            <div className="shrink-0 rounded-2xl border border-teal-200/70 dark:border-teal-900 bg-teal-50/70 dark:bg-teal-950/30 p-4">
              <FolderGit2 className="w-4 h-4 mb-2 text-teal-500" />
              <div className="text-3xl font-bold tracking-tight tabular-nums text-teal-600 dark:text-teal-400">
                {data.repositories.length}
                <span className="text-lg font-semibold text-teal-600/60 dark:text-teal-400/60">
                  /{data.totalInOrg}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">adoption</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">
                  Repository
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">Score</th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">
                  Plan items
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">
                  Automatable
                </th>
                <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider">
                  Last analyzed
                </th>
              </tr>
            </thead>
            <tbody>
              {data.repositories.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-gray-100 dark:border-gray-800 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/30"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/repository/${r.id}`}
                      className="font-medium text-teal-700 dark:text-teal-400 hover:underline"
                    >
                      {r.name}
                    </Link>
                    {r.projectBreakdown?.is_monorepo && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-gray-400">
                        <Boxes className="w-3 h-3" />
                        {r.projectBreakdown.project_count}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-900 dark:text-gray-200">
                    <span className="tabular-nums">{r.latestAnalysisScore ?? '—'}</span>
                    <span
                      className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-[11px] font-semibold ${gradeTone(grade(r.latestAnalysisScore))}`}
                    >
                      {grade(r.latestAnalysisScore)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 tabular-nums">
                    {r.maintenancePlan?.totalItems ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 tabular-nums">
                    {r.maintenancePlan?.automatableCount ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {r.latestAnalysisAt
                      ? new Date(r.latestAnalysisAt).toLocaleDateString()
                      : 'not analyzed'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.repositories.length === 0 && (
            <p className="text-center py-10 text-gray-500 dark:text-gray-400">
              No repositories use this practice.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};
